import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  ViewEncapsulation,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { ConfigurableFocusTrapFactory, ConfigurableFocusTrap } from '@angular/cdk/a11y';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { DomPortal } from '@angular/cdk/portal';

/**
 * Toolbar filter overflow container.
 *
 * Wraps a group of projected filter `<app-select>` controls that live
 * in the toolbar row above a list/table. When the toolbar container is
 * wide enough the filters render inline as normal flex siblings. When
 * the container shrinks below `collapseThreshold` (default 900 px —
 * the "drawer open at default width on a 1440 px viewport" case) the
 * filters collapse into a single "Filters (N)" popover trigger; the
 * projected controls are DOM-portalled into a CDK Overlay panel on
 * open and moved back inline on close.
 *
 * Why measure the parent, not the host: this component's host uses
 * `display: contents` so its projected children stay direct flex
 * siblings of the surrounding `.search-filter-bar`. That means the
 * host itself has no layout box; we walk up to the closest
 * `.search-filter-bar` or `.route-toolbar` and observe that.
 * Falls back to the immediate parent element if no matching
 * ancestor is found (defensive; real callers should always mount
 * inside one of the two toolbar wrappers).
 *
 * Why DOM-portal, not TemplatePortal + `<ng-content>`: `<ng-content>`
 * inside a template projects only when the template is rendered as
 * part of the component's own view. TemplatePortal instantiates the
 * template in a different view, so `<ng-content>` inside would not
 * receive projection. Moving the existing DOM element via DomPortal
 * preserves projected children (they're already parented under our
 * inline wrapper) and returns them home on detach.
 *
 * Freezes the inline↔popover DOM swap while `body.drawer-resizing` is
 * present (mirrors `ResponsiveColumnsDirective`), so a manual drawer
 * drag doesn't flicker the popover across the threshold.
 *
 * A11y: trigger carries `aria-haspopup="dialog"`, `aria-expanded`,
 * and `aria-label="Filters, N active"`. Popover panel is
 * `role="dialog"` with a `ConfigurableFocusTrap` around it (Tab
 * cycles, Esc closes, focus returns to the trigger).
 */
@Component({
  selector: 'app-filter-group',
  imports: [MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Encapsulation.None so the `.app-filter-group-panel` overlay styles
  // (mounted on document.body) can be authored inside this file and
  // reach the portalled DOM without a global stylesheet import.
  encapsulation: ViewEncapsulation.None,
  templateUrl: './filter-group.html',
  styleUrl: './filter-group.css',
  host: {
    '[attr.data-collapsed]': 'collapsed() ? "" : null',
  },
})
export class FilterGroupComponent implements AfterViewInit {
  /** Number of active (non-default) filter values in the group. Drives
   *  the "(N)" badge on the collapsed trigger. Zero hides the badge. */
  readonly activeCount = input(0);

  /** Container-width threshold (px) below which the group collapses
   *  into the popover trigger. Defaults to 900 — chosen so the
   *  drawer-open-at-default-width case (~700 px canvas on
   *  1440 px viewport) reliably collapses filters. */
  readonly collapseThreshold = input(900);

  /** Trigger label. Callers translate at the call site and pass the
   *  translated string in. Defaults to "Filters" — the single-word
   *  affordance recognised across shadcn / Linear / Notion toolbars. */
  readonly label = input('Filters');

  /** Dialog `aria-label` for the popover panel. Callers pass the
   *  translated string; default is a reasonable English fallback. */
  readonly dialogAriaLabel = input('Filter options');

  /** Emitted whenever the popover open state changes — useful for the
   *  route to close its own overlays before this component swaps DOM. */
  readonly openChange = output<boolean>();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly overlay = inject(Overlay);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);
  private readonly focusTrapFactory = inject(ConfigurableFocusTrapFactory);

  protected readonly filterContent =
    viewChild.required<ElementRef<HTMLDivElement>>('filterContent');
  protected readonly triggerButton = viewChild<ElementRef<HTMLButtonElement>>('triggerButton');

  /** Whether the container is narrow enough that filters should
   *  collapse into the popover trigger. Written from the observer
   *  callback outside the zone, then flipped via `zone.run` so the
   *  template can react. */
  protected readonly collapsed = signal(false);

  /** Whether the popover is currently attached to the overlay. */
  protected readonly popoverOpen = signal(false);

  /** Accessible name for the trigger — "Filters" or "Filters, N active". */
  protected readonly triggerAriaLabel = computed(() => {
    const label = this.label();
    const count = this.activeCount();
    return count > 0 ? `${label}, ${count} active` : label;
  });

  private observer: ResizeObserver | null = null;
  private overlayRef: OverlayRef | null = null;
  private focusTrap: ConfigurableFocusTrap | null = null;
  private lastAppliedWidth = -1;
  private previouslyFocusedElement: HTMLElement | null = null;

  ngAfterViewInit(): void {
    // Walk up to the nearest toolbar wrapper (`.search-filter-bar` or
    // `.route-toolbar`) so we measure the same box the user perceives
    // as "the toolbar's width". Falls back to the immediate parent so
    // the component works even without a wrapper ancestor (defensive;
    // real callers should always mount inside one).
    const measured = this.findMeasurementTarget();
    if (!measured) return;

    // Initial measurement — apply synchronously so the first paint
    // lands on the correct branch (inline vs. collapsed).
    this.applyWidth(measured.clientWidth, /* skipDrawerGuard */ true);

    this.zone.runOutsideAngular(() => {
      this.observer = new ResizeObserver(entries => {
        const entry = entries[0];
        const width = entry?.contentBoxSize?.[0]?.inlineSize ?? measured.clientWidth;
        this.applyWidth(width);
      });
      this.observer.observe(measured);
    });

    this.destroyRef.onDestroy(() => {
      this.observer?.disconnect();
      this.observer = null;
      this.closePopover();
    });
  }

  private findMeasurementTarget(): HTMLElement | null {
    const start = this.host.nativeElement.parentElement;
    if (!start) return null;
    let el: HTMLElement | null = start;
    while (el) {
      if (el.classList.contains('search-filter-bar') || el.classList.contains('route-toolbar')) {
        return el;
      }
      el = el.parentElement;
    }
    // No matching ancestor — fall back to the direct parent. Better
    // to measure something than nothing.
    return start;
  }

  private applyWidth(width: number, skipDrawerGuard = false): void {
    if (width === this.lastAppliedWidth) return;
    this.lastAppliedWidth = width;

    // Freeze the DOM swap while the user is manually dragging the
    // drawer edge — otherwise the popover trigger flicker across the
    // 900 px threshold each frame. Mirrors the guard in
    // `ResponsiveColumnsDirective.applyTier`. The initial measurement
    // (`skipDrawerGuard`) is exempt so a drawer-resize-in-progress at
    // mount time doesn't leave us in an uninitialised state.
    if (!skipDrawerGuard && document.body.classList.contains('drawer-resizing')) {
      return;
    }

    const shouldCollapse = width < this.collapseThreshold();
    if (shouldCollapse === this.collapsed()) return;

    // If we're moving from collapsed → inline while the popover is
    // still open, close it first so the DOM portal detaches cleanly
    // before the inline branch re-mounts the trigger button (which
    // would otherwise be `null` at close-time).
    if (!shouldCollapse && this.popoverOpen()) {
      this.zone.run(() => this.closePopover());
    }

    this.zone.run(() => this.collapsed.set(shouldCollapse));
  }

  protected toggleTrigger(event: Event): void {
    event.stopPropagation();
    if (this.popoverOpen()) {
      this.closePopover();
    } else {
      this.openPopover(event.currentTarget as HTMLElement);
    }
  }

  private openPopover(anchor: HTMLElement): void {
    if (this.overlayRef) return;

    const contentEl = this.filterContent().nativeElement;

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(anchor)
      .withPositions([
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'end',
          overlayY: 'top',
          offsetY: 6,
        },
        {
          originX: 'end',
          originY: 'top',
          overlayX: 'end',
          overlayY: 'bottom',
          offsetY: -6,
        },
      ])
      .withPush(true)
      .withViewportMargin(8);

    const overlayConfig: OverlayConfig = {
      positionStrategy,
      // Reposition on scroll so the popover tracks its trigger inside
      // scrollable route containers (matches `<app-select>`).
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      panelClass: 'app-filter-group-panel',
    };

    this.overlayRef = this.overlay.create(overlayConfig);

    // DomPortal moves the existing content DIV into the overlay
    // panel; when disposed, the div returns to its original slot in
    // this component's view. Projected `<app-select>` children ride
    // along because they're real DOM children of the wrapper.
    const portal = new DomPortal(contentEl);
    this.overlayRef.attach(portal);

    // Focus trap: Tab cycles inside the popover, Esc closes. Set up
    // AFTER attach so the overlay element exists.
    this.previouslyFocusedElement = document.activeElement as HTMLElement | null;
    this.focusTrap = this.focusTrapFactory.create(this.overlayRef.overlayElement);
    // `focusInitialElementWhenReady` waits for the DOM to settle
    // before focusing — safer than a naked `focusFirstTabbableElement`
    // which can race with the CDK-attached content.
    this.focusTrap.focusInitialElementWhenReady();

    this.overlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.closePopover());

    this.overlayRef
      .keydownEvents()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        if (event.key === 'Escape') this.closePopover();
      });

    this.popoverOpen.set(true);
    this.openChange.emit(true);
  }

  private closePopover(): void {
    if (!this.overlayRef) return;

    // Destroy the trap BEFORE the portal detaches so it can still
    // read the overlay element it wrapped.
    this.focusTrap?.destroy();
    this.focusTrap = null;

    this.overlayRef.dispose();
    this.overlayRef = null;

    this.popoverOpen.set(false);
    this.openChange.emit(false);

    // Restore focus to the element that opened the popover (usually
    // the trigger button). If it's gone (e.g. the container widened
    // and the trigger re-mounted somewhere else) fall back to the
    // current trigger ref.
    const restore =
      this.previouslyFocusedElement && this.previouslyFocusedElement.isConnected
        ? this.previouslyFocusedElement
        : (this.triggerButton()?.nativeElement ?? null);
    restore?.focus({ preventScroll: true });
    this.previouslyFocusedElement = null;
  }
}
